import type { MessageAction, RequestAction } from "trystero";
import {
  type ElectionCertificate,
  type ElectionRequest,
  type ElectionVote,
  RECOVERY_PACKET_MAX_BYTES,
  type RecoveryHeartbeat,
  type RecoveryStateMachine,
} from "./recovery-protocol.js";
import { decodeBoundedRoomPacket, encodeRoomPacket } from "./room-packet.js";

interface RecoveryActions {
  readonly checkpoint: RequestAction<string, string>;
  readonly certificate: MessageAction<string>;
  readonly heartbeat: MessageAction<string>;
  readonly election: MessageAction<string>;
  readonly vote: MessageAction<string>;
  readonly elected: MessageAction<string>;
  readonly handoff: RequestAction<string, string>;
}

interface RecoveryActionCallbacks {
  readonly state: () => RecoveryStateMachine;
  readonly requireHost: (peerId: string) => void;
  readonly memberId: (peerId: string) => string | undefined;
  readonly heartbeat: (value: RecoveryHeartbeat) => void;
  readonly certified: () => void;
  readonly completeElection: (certificate: ElectionCertificate) => Promise<void>;
  readonly acceptElection: (certificate: ElectionCertificate, peerId: string) => void;
  readonly acceptHandoff: (raw: string, peerId: string) => Promise<string>;
}

export function bindOnlineRecoveryActions(
  actions: RecoveryActions,
  callbacks: RecoveryActionCallbacks,
): void {
  actions.checkpoint.onRequest = (raw, context) => {
    callbacks.requireHost(context.peerId);
    return encodeRoomPacket(
      callbacks.state().offerCheckpoint(decodeBoundedRoomPacket(raw, RECOVERY_PACKET_MAX_BYTES)),
    );
  };
  actions.certificate.onMessage = (raw, context) => {
    callbacks.requireHost(context.peerId);
    callbacks
      .state()
      .installCertifiedCheckpoint(decodeBoundedRoomPacket(raw, RECOVERY_PACKET_MAX_BYTES));
    callbacks.certified();
  };
  actions.heartbeat.onMessage = (raw, context) => {
    callbacks.requireHost(context.peerId);
    callbacks.heartbeat(decodeBoundedRoomPacket<RecoveryHeartbeat>(raw));
  };
  actions.election.onMessage = (raw, context) => {
    const request = decodeBoundedRoomPacket<ElectionRequest>(raw);
    if (callbacks.memberId(context.peerId) !== request.candidateId) return;
    const vote = callbacks.state().vote(request);
    if (vote) void actions.vote.send(encodeRoomPacket(vote), { target: context.peerId });
  };
  actions.vote.onMessage = (raw, context) => {
    const vote = decodeBoundedRoomPacket<ElectionVote>(raw);
    if (callbacks.memberId(context.peerId) !== vote.voterId) return;
    const certificate = callbacks.state().recordElectionVote(vote);
    if (certificate) void callbacks.completeElection(certificate);
  };
  actions.elected.onMessage = (raw, context) => {
    const certificate = decodeBoundedRoomPacket<ElectionCertificate>(raw);
    if (callbacks.memberId(context.peerId) === certificate.hostMemberId)
      callbacks.acceptElection(certificate, context.peerId);
  };
  actions.handoff.onRequest = (raw, context) => callbacks.acceptHandoff(raw, context.peerId);
}
